// The path prefixes Shopify serves an App Proxy under — the "Subpath prefix"
// choices in the app's App proxy settings.
const APP_PROXY_PATH = /^\/(?:apps|a|community|tools)\/[^/]+/;

/**
 * The base URL API requests are actually sent to, given the one the embed
 * was configured with.
 *
 * An App Proxy path is only usable on the origin the customer is browsing:
 * Shopify derives `logged_in_customer_id` from the customer's session on the
 * host the request is sent to, and that session is a cookie of this page's
 * host alone. Aimed at any other host of the same shop, the request carries
 * no customer — and where that host redirects to the shop's primary domain
 * (every non-primary domain does) the browser refuses it outright, because
 * a CORS preflight may not be redirected. That is how the widget broke when
 * the storefront's primary domain changed while the App Embed still named
 * the old one.
 *
 * So a proxy path configured on another origin is read as "this shop's
 * proxy" and resolved against the page's own origin, which is where the
 * same proxy answers for this customer. One embed setting then works on
 * every domain the shop is served from, which a single absolute URL never
 * could.
 *
 * Anything that is not an App Proxy path on a foreign origin is returned
 * untouched: a same-origin or relative proxy path, and a direct API origin
 * (which answers CORS itself).
 */
export function resolveApiBaseUrl(configured: string, pageOrigin: string): string {
  if (!/^https?:\/\//.test(pageOrigin)) {
    return configured;
  }

  let url: URL;
  try {
    url = new URL(configured, pageOrigin);
  } catch {
    return configured;
  }

  if (url.origin === pageOrigin || !APP_PROXY_PATH.test(url.pathname)) {
    return configured;
  }

  return `${pageOrigin}${url.pathname}${url.search}`;
}
